import { getSearchParams } from '@wix/package-picker-shared';
import { useCurrentModule } from '../components';
import { ECLIPSE_MOBILE_MODULE_NAME } from '../utils/moduleNames';
import { useSwitchFlows } from './useSwitchFlows';

/** Desktop only — mobile is rolled out separately, see the MOBILE spec below. */
export const ECLIPSE_PERSONALIZED_CONTENT_SPEC = 'eclipsePersonalizedContent';
export const ECLIPSE_PERSONALIZED_CONTENT_MOBILE_SPEC = 'eclipsePersonalizedContentMobile';
export const ECLIPSE_PERSONALIZATION_BY_QUERY_PARAMS_SPEC = 'eclipsePersonalizationByQueryParams';

/**
 * Shared react-query options for every personalized-content query. The
 * response is a per-session personalization decision, so it never goes stale
 * and never refetches; a failure must fall through to the default copy rather
 * than trip the page's error boundary.
 */
export const PERSONALIZED_CONTENT_QUERY_OPTIONS = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  useErrorBoundary: false
};
/**
 * The public "is personalization on" signal. Surface hooks (title,
 * descriptions, features) should read this directly rather than
 * usePersonalizedParams's `enabled`, which also folds in query-readiness
 * (flow/offering resolved) — a different question than "is the spec on".
 *
 * Desktop and mobile roll out on their own specs, so a surface is on only
 * when the spec for the module it is rendering in is on. The two never
 * substitute for each other: the desktop spec has no effect on mobile, and
 * the mobile spec has none on desktop.
 */
export const useIsPersonalizedContentEnabled = () => {
  const module = useCurrentModule();
  const {
    experiments
  } = module.internals.experiments.hooks.useExperiments();
  const isMobile = module.moduleName === ECLIPSE_MOBILE_MODULE_NAME;
  return experiments.isEnabled(isMobile ? ECLIPSE_PERSONALIZED_CONTENT_MOBILE_SPEC : ECLIPSE_PERSONALIZED_CONTENT_SPEC);
};

// Router-free: showDomain is read in usePersonalizedFeatures so the title and
// description queries don't inherit a <Router> requirement.
export const usePersonalizedParams = () => {
  const module = useCurrentModule();
  const isEnabled = useIsPersonalizedContentEnabled();
  const {
    isUpgradeFlow,
    isSuccess: isFlowResolved,
    isLoading: isFlowLoading,
    isError: isFlowError
  } = useSwitchFlows();
  const {
    siteGuid,
    domainName,
    referralAdditionalInfo,
    subIndustry,
    businessSector,
    businessTermId,
    apps
  } = getSearchParams();
  const isMobile = module.moduleName === ECLIPSE_MOBILE_MODULE_NAME;
  const {
    experiments
  } = module.internals.experiments.hooks.useExperiments();
  const isOverrideEnabled = experiments.isEnabled(ECLIPSE_PERSONALIZATION_BY_QUERY_PARAMS_SPEC);
  const classificationOverride = isOverrideEnabled ? {
    subIndustry,
    businessSector,
    businessTermId,
    apps
  } : undefined;
  const {
    data: offering,
    isSuccess: isOfferingResolved,
    isLoading: isOfferingLoading,
    isError: isOfferingError
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchOffering({});

  // Only the narrowing the optional type forces — the ids are already unique.
  const productFamilyIds = ((offering == null ? void 0 : offering.products) ?? []).map(product => product.productFamilyId).filter(id => Boolean(id));
  return {
    params: {
      siteGuid,
      isUpgrade: isUpgradeFlow,
      domain: domainName,
      referral: referralAdditionalInfo,
      isMobile,
      productFamilyIds,
      ...(classificationOverride ? {
        classificationOverride
      } : {})
    },
    enabled: isEnabled && isFlowResolved && isOfferingResolved && productFamilyIds.length > 0,
    // Follows the queries' own loading state, so a failure settles instead of
    // leaving the caller waiting forever.
    isLoading: isEnabled && (isFlowLoading || isOfferingLoading),
    isError: isEnabled && (isFlowError || isOfferingError)
  };
};
//# sourceMappingURL=usePersonalizedParams.js.map